import React, { memo } from "react";
import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { DisaggregatedMetric } from "../../services/types";
import { formatValue } from "../../utils/helpers";

interface PDFEthnicityChartProps {
    indicators: DisaggregatedMetric[] | null;
    title?: string;
    icon?: React.FC<{ width?: number; height?: number }> | null;
    barColor: string;
}

const styles = StyleSheet.create({
    container: { width: '48%' },
    barRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
    label: { width: 80, fontSize: 8, color: '#3A2E29' },
    value: { width: 20, fontFamily: 'Helvetica-Bold', fontSize: 8, color: '#3A2E29', marginLeft: 8 },
    barBackground: { flex: 1, height: 10, backgroundColor: '#FFFFFF', marginLeft: 4, position: 'relative' },
    barFill: { height: 10 },
    metricCard: {
        width: '48%',
        marginBottom: 16,
        paddingRight: 4,
    },
    metricTitle: { fontSize: 10, fontWeight: 700, color: '#3A2E29' },
    metricYear: { fontSize: 10, marginTop: 2, marginBottom: 8, color: '#3A2E29' },
});

const PDFEthnicityChart = memo(({ indicators, title, icon, barColor }: PDFEthnicityChartProps) => {
    if (!indicators) return [];
    const indicatorData = indicators.find(d => d.id === 1072);
    if (!indicatorData) return [];
    const version = indicatorData.versions[0];
    if (!version) return [];

    const indicatorVersion = indicatorData.versions[0];
    const populationMetric = indicators.find(m => m.id === 1088); // Population
    const populationVersion = populationMetric?.versions[0];

    if (!indicatorVersion || !populationVersion || !populationVersion.value) return null;

    const hispanicValue = indicatorVersion.value / populationVersion.value;
    const nonHispanicValue = 1 - hispanicValue;

    const year = indicatorVersion.year;

    const chartData = [
        { label: 'Hispanic or Latino', value: hispanicValue },
        { label: 'Non Hispanic or Latino', value: nonHispanicValue },
    ];

    const maxValue = Math.max(...chartData.map(d => d.value)) || 1;

    return (
        <View style={styles.metricCard}>
            {icon && React.createElement(icon, { width: 30, height: 30 })}
            <Text style={styles.metricTitle}>{title ?? "Ethnicity - Hispanic/Latino"}</Text>
            <Text style={styles.metricYear}>in {year}</Text>
            {chartData.map(d => (
                <View key={d.label} style={styles.barRow}>
                    <Text style={styles.label}>{d.label}</Text>
                    <Text style={styles.value}>{formatValue(d.value, 'percentage', 1072)}</Text>
                    <View style={styles.barBackground}>
                        <View style={[styles.barFill, { width: `${(d.value / maxValue) * 100}%`, backgroundColor: barColor }]} />
                    </View>
                </View>
            ))}
        </View>
    );
});

export default PDFEthnicityChart;